import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSet } from 'react-use';
import findIndex from 'lodash/findIndex';
import sum from 'lodash/sum';

import useRect from '@lyearn/core/hooks/useRect';
import { useTranslation } from '@lyearn/i18n';
import { NucleoGripDotsVertical } from '@lyearn/icons';
import { Stack } from '@lyearn/ui';
import { ColumnShape } from '@/components/Table/types';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import * as ActionItem from '@/modules/tasks/features/actionItems/components/ActionItem';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';
import TaskEntity from '@/modules/tasks/task.entity';
import routes from '@/pages/corporate/routes/tasks';
import {
  ActionItemPriorityEnum,
  ActionItemType,
  CreateActionItemInput,
  EntityTagType,
  RelatedEntityTypeEnum,
  UserViewFieldEntityTypeEnum,
  UserViewFieldSchema,
} from '@/types/schema';

import { DashboardActions } from '../../../../hooks/useDashboardActions';
import useTaskListActionMenu from '../../../../hooks/useTaskListActionMenu';
import { DashboardActionItemFragmentWithChildCount, GroupedActionItem } from '../../../../types';
import getInitialTask from '../../../../utils/getInitalTasks';
import { TaskGroupByEnum } from '../../../TaskDashboard/types';
import { isGroupedByTag } from '../../../TaskGroupByFilter/utils';
import TaskTags from '../../../TaskTags';
// import AddNewTask from '../../components/AddNewTask';
import LoadMore from '../../components/LoadMore';
import LocalActionItemOptions from '../../components/LocalActionItemOptions';
import LocalActionItemProvider from '../../components/LocalActionItemProvider';
import SubtaskListing from '../../components/SubtaskListing';
import TableHeader from '../../components/TableHeader';
import TaskCheckbox from '../../components/TaskCheckbox';
import GroupHeaderCheckbox from '../../components/TaskCheckbox/GroupHeaderCheckbox';
import TimeTracked from '../../components/TimeTracked';
import Title from '../../components/Title';
import { DEFAULT_WIDTHS, MIN_WIDTHS, TASK_LIST_SIZE_CONFIG } from '../../consts';
import {
  GroupedListProps,
  GroupedTableGroupData,
  SortAndFilterType,
  TableRowData,
  TableRowFetcher,
  TableRowType,
  TableRowWithData,
} from '../../types';
import {
  ExpandedRowKeys,
  getGroupedByStage,
  getGroupedByStatus,
  getGroupedByTag,
  getGroupedByUser,
} from '../../utils';
import { setTasksByParentIdsType } from '../useTasksNesting';
import StatusLoader from './StatusLoader';
import TitleLoader from './TitleLoader';

const ASSIGNED_TO_PROFILE_SETTINGS = {
  showName: false,
  classes: { name: 'text-text-secondary' },
};

export const MAPPED_PRIORITY: Record<string, ActionItemPriorityEnum> = {
  high: ActionItemPriorityEnum.High,
  low: ActionItemPriorityEnum.Low,
  medium: ActionItemPriorityEnum.Medium,
  noPriority: ActionItemPriorityEnum.NoPriority,
  urgent: ActionItemPriorityEnum.Urgent,
};

export const REVERSE_MAPPED_PRIORITY: Record<ActionItemPriorityEnum, string> = {
  [ActionItemPriorityEnum.High]: 'high',
  [ActionItemPriorityEnum.Low]: 'low',
  [ActionItemPriorityEnum.Medium]: 'medium',
  [ActionItemPriorityEnum.NoPriority]: 'noPriority',
  [ActionItemPriorityEnum.Urgent]: 'urgent',
};

export const UNASSIGNED_KEY = 'UnAssigned';
export const UNSTAGED_KEY = 'UnStaged';
// TODO: @aryan02420 add support for tags
export const getGroupByProps = ({
  groupBy,
  groupByKey,
  groupedByStatus,
  groupedByTag,
}: {
  groupBy?: string;
  groupByKey?: string;
  groupedByStatus?: Pick<ActionItemType, 'status' | 'statusId' | 'category'> | null;
  groupedByTag?: EntityTagType | null;
}): Partial<CreateActionItemInput> | undefined => {
  if (!groupBy || !groupByKey) {
    return undefined;
  }
  if (isGroupedByTag(groupBy)) {
    return {
      tags: (groupedByTag ? [groupedByTag] : []) as any, //DO LATER: write a common type for ActionItemFragment and CreateActionItemInput to handle this in AddOrEditTaskDialog
    };
  }

  switch (groupBy) {
    case TaskGroupByEnum.Status:
      return {
        statusId: groupedByStatus?.statusId,
        status: groupedByStatus?.status,
        category: groupedByStatus?.category,
      };
    case TaskGroupByEnum.Assignee:
      if (groupByKey === UNASSIGNED_KEY) {
        return undefined;
      }
      return { assignedToIds: [groupByKey] };
    case TaskGroupByEnum.Priority:
      return {
        priority: MAPPED_PRIORITY[groupByKey] || ActionItemPriorityEnum.NoPriority,
      };
    case TaskGroupByEnum.Stage:
      if (groupByKey === UNSTAGED_KEY) {
        return undefined;
      }
      return {
        stageIds: [groupByKey],
      };
    case TaskGroupByEnum.DueDate:
      // since date ranges are grouped, we cannot pick a specific date here
      return undefined;
    default:
      return undefined;
  }
};

const getRowFetcher = (parentId: string): TableRowFetcher => ({
  id: `row-fetcher-${parentId}`,
  parentId,
  type: TableRowType.Fetcher,
});

type useGroupedTableProps = GroupedListProps & {
  tasksByParentIds: Record<string, DashboardActionItemFragmentWithChildCount[]>;
  setTasksByParentIds: setTasksByParentIdsType;
  onFetchChildActionItems: (parentId: string, sortAndFilter: SortAndFilterType) => void;
  disableInteractiveTooltip?: boolean;
};

const useGroupedTable = (props: useGroupedTableProps) => {
  const { t } = useTranslation();
  const { ref: containerRef, width: containerWidth } = useRect<HTMLDivElement>();
  const { disableInteractiveTooltip = true } = props;

  const {
    groupedActionItems,
    actions,
    providers,
    setInViewTask,
    columnWidth,
    sortedTaskViewConfig,
    participants,
    allTags,
    dispatch,
    entity,
    entityId,
    setColumnWidth,
    fetchMore,
    defaultAssigneeId,
    fileId,
    groupBy,
    disableAddTask,
    fetchingMoreGroupKey,
    entityName,
    addTasksViaAI,
    tasksByParentIds,
    setTasksByParentIds,
    onFetchChildActionItems,
    sortAndFilter,
    files,
    systemGeneratedFiles,
    subTaskView,
  } = props;

  const {
    [FeatureFlags.EnableProjectManagementPreReleaseFeatures]: isProjectManagementPreReleaseEnabled,
  } = useFeatureFlags();

  const [collapsedGroups, { toggle: toggleCollapsedGroup }] = useSet();
  const [expandedRowKeys, setExpandedRowKeys] = useState<ExpandedRowKeys>([]);
  const onExpandedRowsChange = useCallback((expandedKeys: ExpandedRowKeys) => {
    setExpandedRowKeys(expandedKeys);
  }, []);
  const sizeConfig = TASK_LIST_SIZE_CONFIG.default;

  const draggingItemRef = useRef<ActionItemFragment | null>(null);

  const visibleTaskConfig = useMemo(
    () => sortedTaskViewConfig?.filter((config) => config.visible),
    [sortedTaskViewConfig],
  );
  const { id } = useUserContext();
  const userId = defaultAssigneeId || id;

  const onColumnResize = useCallback(
    async function columnResize({
      column,
      width,
    }: {
      column: ColumnShape<TableRowData>;
      width: number;
    }) {
      const newColumnWidth = {
        ...columnWidth,
        [column.key as string]: width,
      };

      setColumnWidth(newColumnWidth);
    },
    [columnWidth, setColumnWidth],
  );

  const { getActionMenu } = useTaskListActionMenu({
    setInViewTask,
    dispatch,
    onDeleteActionItem: actions.onDeleteActionItem,
    entity,
    entityId,
  });

  const { tablesData } = useMemo(() => {
    const tablesData: {
      groupData: GroupedTableGroupData;
      data: TableRowData[];
    }[] = [];

    const getChildActionItems = (
      actionItem: DashboardActionItemFragmentWithChildCount,
      groupByKey: string,
    ): (TableRowWithData | TableRowFetcher)[] => {
      const parentId = actionItem.id;
      if (!parentId) {
        return [];
      }
      const childActionItems = tasksByParentIds[parentId];

      if (!childActionItems) {
        if (actionItem.childCount > 0) {
          return [getRowFetcher(parentId)];
        } else {
          return [];
        }
      }

      // has child data
      return (childActionItems || []).map((childActionItem) => ({
        id: `${childActionItem.id}`,
        type: TableRowType.Data,
        data: {
          ...childActionItem,
          ...{ showParentName: false },
        },
        children: getChildActionItems(childActionItem, groupByKey),
        childCount: childActionItem.childCount,
        groupByKey,
      }));
    };

    groupedActionItems.forEach((groupedActionItem, i) => {
      tablesData.push({
        groupData: {
          groupBy,
          groupByKey: groupedActionItem.groupByKey,
          groupedByUser: getGroupedByUser({
            groupBy,
            sortedTaskViewConfig,
            groupedActionItem,
            groupByKey: groupedActionItem.groupByKey,
          }),
          groupedByStatus: getGroupedByStatus({
            groupBy,
            groupedActionItem,
          }),
          groupedByStage: getGroupedByStage({
            groupBy,
            groupedActionItem,
          }),
          groupedByTag: getGroupedByTag({
            groupBy,
            sortedTaskViewConfig,
            groupedActionItem,
          }),
          totalCount: groupedActionItem.totalCount,
        },
        data: [],
      });

      groupedActionItem.actionItems.map((actionItem) => {
        const childrenActionItems: (TableRowWithData | TableRowFetcher)[] = getChildActionItems(
          actionItem,
          groupedActionItem.groupByKey,
        );

        tablesData[i].data.push({
          id: `${actionItem.id}`,
          type: TableRowType.Data,
          data: {
            ...actionItem,
            ...{ showParentName: true },
          },
          ...(childrenActionItems?.length ? { children: childrenActionItems } : {}),
          childCount: actionItem.childCount,
          groupByKey: groupedActionItem.groupByKey,
        });
      });

      if (groupedActionItem.pageInfo?.hasNextPage) {
        tablesData[i].data.push({
          id: `${groupedActionItem.groupByKey}-load-more`,
          type: TableRowType.LoadMore,
          data: groupedActionItem,
        });
      }

      if (
        !disableAddTask &&
        !(
          entity &&
          [RelatedEntityTypeEnum.Meeting, RelatedEntityTypeEnum.TeamMeeting].includes(entity)
        )
      ) {
        // disabled for now since we are showing load more button. once load more is removed, we can enable this
        // tablesData[i].data.push({
        //   id: `${groupedActionItem.groupByKey}-add-new-task`,
        //   type: TableRowType.AddNewTask,
        //   data: groupedActionItem,
        // });
      }
    });

    return { tablesData };
  }, [disableAddTask, entity, groupBy, groupedActionItems, sortedTaskViewConfig, tasksByParentIds]);

  const fetchMoreActionItems = useCallback(
    (groupedActionItem: GroupedActionItem) => {
      if (groupedActionItem.pageInfo?.hasNextPage) {
        fetchMore?.(groupedActionItem);
      }
    },
    [fetchMore],
  );

  const {
    [FeatureFlags.EnableTaskTimer]: isTaskTimerEnabled,
    [FeatureFlags.EnableTaskBulkOperations]: isTaskBulkOperationsEnabled,
  } = useFeatureFlags();

  const COMPONENTS: Record<string, (config?: UserViewFieldSchema) => ColumnShape<TableRowData>> =
    useMemo(
      () => ({
        move: () => ({
          key: 'move',
          width: DEFAULT_WIDTHS.move,
          minWidth: MIN_WIDTHS.move,
          headerClassName: sizeConfig.Move.className,
          className: sizeConfig.Move.className,
          align: 'center',
          headerRenderer: () => null,
          cellRenderer: (val) => {
            return <NucleoGripDotsVertical className="w-18 h-18" fillColorToken="icon-secondary" />;
          },
          resizable: false,
          flexShrink: 0,
        }),
        expand: () => ({
          key: 'expand',
          width: DEFAULT_WIDTHS.expand,
          minWidth: MIN_WIDTHS.expand,
          headerClassName: sizeConfig.Expand.className,
          className: sizeConfig.Expand.className,
          align: 'center',
          headerRenderer: () => null,
          cellRenderer: (val) => null,
          resizable: false,
          flexShrink: 0,
        }),
        status: () => ({
          key: 'status',
          dataKey: 'status',
          width: DEFAULT_WIDTHS.status,
          minWidth: MIN_WIDTHS.status,
          headerClassName: sizeConfig.Status.className,
          className: sizeConfig.Status.className,
          align: 'center',
          headerRenderer: () => null,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.Status size={sizeConfig.Status.size} />;
            }

            if (val.rowData.type === TableRowType.LoadMore) {
              if (fetchingMoreGroupKey === val.rowData.data.groupByKey) {
                return <StatusLoader />;
              }

              return null;
            }
          },
          flexShrink: 0,
        }),
        title: () => ({
          key: 'title',
          dataKey: 'title',
          width: columnWidth['title'] ?? DEFAULT_WIDTHS['title'],
          minWidth: MIN_WIDTHS['title'],
          headerClassName: '!overflow-visible', // Using '!overflow-visible' since "Name" heading has negative margin on the left to align with the previous column
          className: sizeConfig.Title.className,
          headerRenderer: () => (
            <TableHeader className={sizeConfig.Title.headerCellClassName} text={t('Common:Name')} />
          ),
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              const { data } = val.rowData;
              const viewTask = () => setInViewTask(data.id);
              const taskLink = routes.TasksIssue(data.id, data);
              const onExpand = () => {
                const isExpanded = expandedRowKeys.includes(data.id);
                const newExpandedKeys = isExpanded
                  ? expandedRowKeys.filter((key) => key !== data.id)
                  : [...expandedRowKeys, data.id];
                onExpandedRowsChange(newExpandedKeys);
              };

              return (
                <Title
                  childCount={val.rowData.childCount}
                  disableInteractiveTooltip={disableInteractiveTooltip}
                  inViewFileId={fileId}
                  taskLink={taskLink}
                  variant={sizeConfig.Title.variant}
                  onExpand={onExpand}
                  onViewTask={viewTask}
                />
              );
            }

            if (val.rowData.type === TableRowType.LoadMore) {
              const groupedActionItem = val.rowData.data;

              if (fetchingMoreGroupKey === groupedActionItem.groupByKey) {
                return <TitleLoader />;
              }

              return (
                <LoadMore
                  className={sizeConfig.Row.className}
                  fetchMoreActionItems={fetchMoreActionItems}
                  groupedActionItem={groupedActionItem}
                  variant={sizeConfig.Actions.variant}
                />
              );
            }
          },
          resizable: true,
          flexShrink: 0,
        }),
        selection: () => ({
          key: 'selection',
          dataKey: 'selection',
          width: DEFAULT_WIDTHS.selection,
          minWidth: MIN_WIDTHS.selection,
          className: sizeConfig.Selection.className,
          headerClassName: sizeConfig.Selection.className,
          align: 'center',
          headerRenderer: ({ container }) => {
            const tableData = container.props.data;
            const firstRow = tableData?.[0];
            if (!firstRow || firstRow.type !== TableRowType.Data) {
              return null;
            }
            return (
              <GroupHeaderCheckbox
                className="mx-8"
                groupByKey={firstRow.groupByKey}
                groupHoverClassName="opacity-0 group-hover:opacity-100"
              />
            );
          },
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return (
                <TaskCheckbox
                  actionItemId={val.rowData.data.id}
                  groupByKey={val.rowData.groupByKey}
                  groupHoverClassName="opacity-0 group-hover:opacity-100"
                  isRootTask={TaskEntity.isRootTask(val.rowData.data)}
                  subTaskView={subTaskView}
                />
              );
            }

            if (val.rowData.type === TableRowType.LoadMore) {
              return null;
            }
          },
          resizable: false,
          flexShrink: 0,
        }),
        timer: () => ({
          key: 'timer',
          dataKey: 'timer',
          width: isTaskTimerEnabled ? columnWidth['timer'] ?? DEFAULT_WIDTHS['timer'] : 0,
          minWidth: isTaskTimerEnabled ? MIN_WIDTHS['timer'] : 0,
          headerRenderer: () => {
            if (isTaskTimerEnabled) {
              return <TableHeader className="ps-12" text="Time Tracked" />;
            }
            return null;
          },
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return (
                <TimeTracked
                  disableInteractiveTooltip={disableInteractiveTooltip}
                  rowData={val.rowData}
                />
              );
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        priority: () => ({
          key: 'priority',
          dataKey: 'priority',
          width: columnWidth['priority'] ?? DEFAULT_WIDTHS['priority'],
          minWidth: MIN_WIDTHS['priority'],
          headerRenderer: () => (
            <TableHeader className="ps-12" text={t('Common:Filter.Priority')} />
          ),
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.Priority hideLabel />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        list: () => ({
          key: 'list',
          dataKey: 'list',
          width: columnWidth['list'] ?? DEFAULT_WIDTHS['list'],
          minWidth: MIN_WIDTHS['list'],
          headerRenderer: () => <TableHeader className="ps-12" text="List" />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.List />;
            }
            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        project: () => ({
          key: 'project',
          dataKey: 'project',
          width: columnWidth['project'] ?? DEFAULT_WIDTHS['project'],
          minWidth: MIN_WIDTHS['project'],
          headerRenderer: () => <TableHeader className="ps-12" text="Project" />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.Project />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        dueDate: () => ({
          key: 'dueDate',
          dataKey: 'dueDate',
          width: columnWidth['dueDate'] ?? DEFAULT_WIDTHS['dueDate'],
          minWidth: MIN_WIDTHS['dueDate'],
          headerRenderer: () => <TableHeader className="ps-12" text={t('Task:Details.DueDate')} />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.DatePicker />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        createdOn: () => ({
          key: 'createdOn',
          dataKey: 'createdOn',
          width: columnWidth['createdOn'] ?? DEFAULT_WIDTHS['createdOn'],
          minWidth: MIN_WIDTHS['createdOn'],
          headerRenderer: () => (
            <TableHeader className="ps-12" text={t('Task:Details.CreatedOn')} />
          ),
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.CreatedOn />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        assignee: () => ({
          key: 'assignee',
          dataKey: 'assignedTo',
          width: columnWidth['assignee'] ?? DEFAULT_WIDTHS['assignee'],
          minWidth: MIN_WIDTHS['assignee'],
          headerRenderer: () => (
            <TableHeader className="ps-12" text={t('Common:Filter.Assignee')} />
          ),
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              if (participants?.length) {
                return <ActionItem.StaticAssignMenu participants={participants} />;
              }

              return (
                <ActionItem.AssignMenu
                  className="truncate"
                  profileSettings={ASSIGNED_TO_PROFILE_SETTINGS}
                />
              );
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        stage: () => ({
          key: 'stage',
          dataKey: 'stage',
          width: columnWidth['stage'] ?? DEFAULT_WIDTHS['stage'],
          minWidth: MIN_WIDTHS['stage'],
          headerRenderer: () => <TableHeader className="ps-12" text={t('Task:Stage')} />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.Stage />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        [UserViewFieldEntityTypeEnum.Tag]: (config?: UserViewFieldSchema) => ({
          key: config?.label!,
          dataKey: config?.label,
          width:
            columnWidth[config?.label!] ||
            columnWidth[UserViewFieldEntityTypeEnum.Tag] ||
            DEFAULT_WIDTHS[UserViewFieldEntityTypeEnum.Tag],
          minWidth: MIN_WIDTHS[config?.label!] || MIN_WIDTHS[UserViewFieldEntityTypeEnum.Tag],
          headerRenderer: () => <TableHeader className="ps-12" text={config?.name!} />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              if (config) {
                return (
                  <TaskTags
                    actionItem={val.rowData.data}
                    allTags={allTags}
                    config={config}
                    disableInteractiveTooltip={disableInteractiveTooltip}
                  />
                );
              }
              return null;
            }

            return null;
          },

          resizable: true,
          flexShrink: 0,
        }),
        linkedTo: () => ({
          key: 'linkedTo',
          dataKey: 'linkedToEntities',
          width: columnWidth['linkedTo'] ?? DEFAULT_WIDTHS['linkedTo'],
          minWidth: MIN_WIDTHS['linkedTo'],
          headerRenderer: () => <TableHeader text={t('Task:Details.Linkedto')} />,
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return <ActionItem.LinkedTo />;
            }

            return null;
          },
          resizable: true,
          flexShrink: 0,
        }),
        options: () => ({
          key: 'options',
          dataKey: 'options',
          width: DEFAULT_WIDTHS['options'],
          minWidth: MIN_WIDTHS['options'],
          align: 'right',
          cellRenderer: (val) => {
            if (val.rowData.type === TableRowType.Data) {
              return (
                <LocalActionItemOptions getActionMenu={getActionMenu} rowData={val.rowData.data} />
              );
            }

            return null;
          },
          flexShrink: 0,
        }),
      }),
      [
        allTags,
        columnWidth,
        disableInteractiveTooltip,
        expandedRowKeys,
        fetchMoreActionItems,
        fetchingMoreGroupKey,
        fileId,
        getActionMenu,
        isTaskTimerEnabled,
        onExpandedRowsChange,
        participants,
        setInViewTask,
        sizeConfig,
        subTaskView,
        t,
      ],
    );

  const columns = useMemo(() => {
    const columnsData = [];

    if (isProjectManagementPreReleaseEnabled) {
      columnsData.push(COMPONENTS['move']());
    }
    if (isTaskBulkOperationsEnabled) {
      columnsData.push(COMPONENTS['selection']());
    }
    columnsData.push(COMPONENTS['expand']());
    columnsData.push(COMPONENTS['status']());
    for (const config of visibleTaskConfig ?? []) {
      if (config.entityType === UserViewFieldEntityTypeEnum.Tag) {
        columnsData.push(COMPONENTS[UserViewFieldEntityTypeEnum.Tag](config));
      } else if (COMPONENTS[config.label]) {
        columnsData.push(COMPONENTS[config.label](config));
      }
    }
    columnsData.push(COMPONENTS['options']());

    return columnsData;
  }, [
    COMPONENTS,
    isTaskBulkOperationsEnabled,
    visibleTaskConfig,
    isProjectManagementPreReleaseEnabled,
  ]);

  const expandColumnKeyIndex = useMemo(() => findIndex(columns, { key: 'expand' }), [columns]);

  const rowRenderer = useCallback(
    ({ cells, rowData }: { cells: React.ReactNode; rowData: TableRowData }) => {
      if (rowData.type === TableRowType.Fetcher) {
        return rowData?.parentId ? (
          <SubtaskListing
            actions={actions}
            cells={cells}
            className={sizeConfig.Row.className}
            parentId={rowData.parentId}
            providers={providers}
            setInViewTask={setInViewTask}
            sizeConfig={sizeConfig}
            onFetchChildActionItems={(parentId) => onFetchChildActionItems(parentId, sortAndFilter)}
          />
        ) : null;
      }
      if (rowData.type === TableRowType.Data) {
        return (
          <LocalActionItemProvider
            actions={actions}
            className={sizeConfig.Row.className}
            disableInteractiveTooltip={disableInteractiveTooltip}
            draggingItemRef={draggingItemRef}
            groupBy={groupBy}
            providers={providers}
            rowData={rowData.data}>
            {cells}
          </LocalActionItemProvider>
        );
      }

      if (rowData.type === TableRowType.AddNewTask) {
        return <Stack className={sizeConfig.Row.className} />;
      }

      return cells;
    },
    [
      actions,
      disableInteractiveTooltip,
      groupBy,
      onFetchChildActionItems,
      providers,
      setInViewTask,
      sizeConfig,
      sortAndFilter,
    ],
  );

  const onAddTaskToGroup = useCallback(
    (groupData: GroupedTableGroupData) => {
      dispatch({
        type: DashboardActions.ADD_NEW_TASK,
        payload: {
          task: {
            ...getInitialTask({
              userId,
              fileId,
              entity,
              entityId,
            }),
            ...getGroupByProps(groupData),
          },
        },
      });
    },
    [dispatch, userId, fileId, entity, entityId],
  );

  const totalColumnWidth = useMemo(
    () => sum(columns.map((c) => Math.max(c.width, c.minWidth ?? 0))),
    [columns],
  );
  const tableWidth = Math.max(
    totalColumnWidth + sizeConfig.ExtraWidthForActions,
    containerWidth ?? 0,
  );

  return {
    containerRef,
    tablesData,
    columns,
    rowRenderer,
    sizeConfig,
    tableWidth,
    onColumnResize,
    onAddTaskToGroup,
    collapsedGroups,
    toggleCollapsedGroup,
    expandedRowKeys,
    onExpandedRowsChange,
    expandColumnKeyIndex,
  };
};

export default useGroupedTable;
